import { useEffect, useState } from "react";
import { apiGet } from "lib/apiClient";
import { formatKes } from "lib/format";

const SOURCE_LABEL = {
  DEPOSIT_FEE: "Deposit fees",
  DEPOSIT_FEE_FIXED: "Deposit SMS fees",
  WITHDRAWAL_FEE: "Withdrawal fees",
  TRADING_FEE: "Trading fees",
  TRADING_COMMISSION: "Commission on profits",
  MARKET_SETTLEMENT_SPREAD: "Settlement surplus",
};

export default function SettlementBreakdown({ url, className = "" }) {
  const [state, setState] = useState({ data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    apiGet(url)
      .then((data) => {
        if (!cancelled) setState({ data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ data: null, error: err.message || "Could not load the breakdown." });
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (state.error) return <p className={`text-xs text-red-500 ${className}`}>{state.error}</p>;
  if (!state.data) return <div className={`h-24 animate-pulse rounded-md ${className} bg-accent`} />;
  const { lines, totals, currency } = state.data;
  const c = currency || "KES";
  const cell = "num whitespace-nowrap py-1.5 pl-3 text-right";

  return (
    <div className={className}>
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
        <table className="w-full min-w-[420px] text-left text-sm">
          <thead>
            <tr className="text-xs text-gray-500">
              <th className="px-3 py-2 font-semibold">Source</th>
              <th className="py-2 pl-3 text-right font-semibold">Gross</th>
              <th className="py-2 pl-3 text-right font-semibold">Seed returned</th>
              <th className="py-2 pl-3 pr-3 text-right font-semibold">Net</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-3 text-center text-xs text-gray-500">
                  No revenue events are attached to this settlement.
                </td>
              </tr>
            )}
            {lines.map((l) => (
              <tr key={l.sourceType} className="border-t border-gray-100 dark:border-white/5">
                <td className="px-3 py-1.5 text-navy-700 dark:text-white">
                  {SOURCE_LABEL[l.sourceType] || l.sourceType}
                  <span className="ml-1 text-xs text-gray-400">({l.eventCount})</span>
                </td>
                <td className={`${cell} text-navy-700 dark:text-gray-200`}>{formatKes(l.gross, c)}</td>
                <td className={`${cell} text-gray-600 dark:text-gray-300`}>{formatKes(l.seedReturned, c)}</td>
                <td className={`${cell} pr-3 font-semibold text-navy-700 dark:text-white`}>{formatKes(l.net, c)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-gray-200 font-semibold dark:border-white/10">
              <td className="px-3 py-2 text-navy-700 dark:text-white">Total</td>
              <td className={`${cell} text-navy-700 dark:text-white`}>{formatKes(totals.gross, c)}</td>
              <td className={`${cell} text-navy-700 dark:text-white`}>{formatKes(totals.seedReturned, c)}</td>
              <td className={`${cell} pr-3 text-navy-700 dark:text-white`}>{formatKes(totals.net, c)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
        Gross is revenue after payment processing. Seed returned is a market's own opening seed coming back inside its settlement surplus, and is not
        revenue.
        {Number(totals.otherDeductions) > 0 ? ` Other deductions: ${formatKes(totals.otherDeductions, c)}.` : ""}
      </p>
    </div>
  );
}
